// ============================================================
// App — router + AuthProvider + protected routes
// ============================================================

import { BrowserRouter, HashRouter, Link, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { DashboardLayout } from './layouts/DashboardLayout';
import { OverviewPage } from './pages/OverviewPage';
import { UsersPage } from './pages/UsersPage';
import { CommunitiesPage } from './pages/CommunitiesPage';
import { CommunityRoles } from './pages/CommunityRoles';
import { CommunityMembers } from './pages/CommunityMembers';
import { ChannelPermissions } from './pages/ChannelPermissions';
import { AuditLogPage } from './pages/AuditLogPage';
import { ModerationPage } from './pages/ModerationPage';
import { BansPage } from './pages/BansPage';
import { InvitesPage } from './pages/InvitesPage';
import { InstancePage } from './pages/InstancePage';
import { HealthPage } from './pages/HealthPage';
import { SiverixServersPage } from './pages/SiverixServersPage';
import { SsoConsentPage } from './pages/SsoConsentPage';
import { SsoReturnPage } from './pages/SsoReturnPage';
import { ThemeToggle } from './theme/ThemeToggle';

// [2.2] Servi sous /admin/ par le serveur tout-en-un (build `--base /admin/`),
// les routes de l'admin (/admin/users, /admin/communities…) entreraient en
// collision avec les routes API du même nom au rechargement : routeur par
// hash dans ce cas. À la racine (dev, e2e, conteneur dédié) : inchangé.
const Router = import.meta.env.BASE_URL === '/' ? BrowserRouter : HashRouter;

export function App(): JSX.Element {
  return (
    <Router>
      <AuthProvider>
        <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 9999 }}>
          <ThemeToggle />
        </div>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          {/* [2.13.0] Serveur de comptes : consentement (connecté, pas forcément admin). */}
          <Route
            path="/sso"
            element={
              <ProtectedRoute>
                <SsoConsentPage />
              </ProtectedRoute>
            }
          />
          {/* [2.13.0] Serveur Siverix : retour du serveur de comptes avec l'assertion. */}
          <Route path="/sso-retour" element={<SsoReturnPage />} />
          <Route
            element={
              <ProtectedRoute requireAdmin>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<OverviewPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/communities" element={<CommunitiesPage />} />
            <Route path="/communities/:id/roles" element={<CommunityRoles />} />
            <Route path="/communities/:id/members" element={<CommunityMembers />} />
            <Route path="/communities/:id/channels-acl" element={<ChannelPermissions />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/moderation" element={<ModerationPage />} />
            <Route path="/bans" element={<BansPage />} />
            <Route path="/invites" element={<InvitesPage />} />
            <Route path="/instance" element={<InstancePage />} />
            <Route path="/health" element={<HealthPage />} />
            <Route path="/siverix-servers" element={<SiverixServersPage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

function NotFound(): JSX.Element {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', textAlign: 'center' }}>
      <div>
        <h1 style={{ marginBottom: 8 }}>404</h1>
        <p style={{ color: 'var(--color-text-muted)' }}>Cette page n'existe pas.</p>
        <Link to="/" style={{ color: 'var(--color-accent)' }}>
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
